import { Loader } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityType } from '../../../api/common/types';
import {
  useAvailableTaskTypeOptions,
  useAvailableTaskTypes,
} from '../../../api/task-types-client/task-types-client.hooks';
import { getEntityType } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import { setSelectedTaskType } from '../../../store/process-analysis-store/process-analysis-store.actions';
import { Select } from '../../../ui-library/select/select';

const ALL_TYPES_VALUE = '__all__';

/**
 * Single-select dropdown that filters every Process Analysis metric by a
 * single normalized Task.type value. Persists via the PA store, applies to all
 * sub-tabs and to the comparison side. "All work item types" clears the
 * filter. Renders the shared `ui-library` Select so it matches the sibling
 * Allocation Type filter in the sidebar.
 *
 * Option VALUES stay on the legacy contract (canonical type strings from the
 * bare-array endpoint) because Process Analysis metric scoping matches
 * non-canonical values as `type=UNKNOWN, raw_type__iexact` — feeding raw_type
 * keys here would zero out every metric. That same case-insensitive matching
 * makes case-variant values interchangeable filter keys, so the options are
 * deduped case-insensitively. The labeled endpoint is queried in parallel
 * purely as a display lookup: when it carries a verbatim external label for
 * a matching value (UNKNOWN/raw entries like "qa" → "QA"), that label is
 * shown; otherwise the legacy title-case rendering is kept. Distinct values
 * that end up sharing a display label are disambiguated with the raw value
 * in parentheses, and a small spinner replaces the chevron while the values
 * query is in flight.
 *
 * @returns {JSX.Element} The Select control
 */
export const TaskTypeFilter = () => {
  const { t } = useTranslation();
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const { entityType, entityId } = useResolvedEntity();
  const { taskTypes, query: taskTypesQuery } = useAvailableTaskTypes({ entityType, entityId });
  const { taskTypeOptions } = useAvailableTaskTypeOptions({ entityType, entityId });

  const allTypesLabel = t('process_analysis.task_type_filter.all', {
    defaultValue: 'All work item types',
  });

  // Value→verbatim-label lookup from the labeled endpoint, keyed by the
  // LOWERCASED value so labels attach across case-variant raw_type rows (and
  // across the backend's move to lowercased option values). The drop-equal
  // filter stays EXACT on purpose: comparing case-insensitively would also
  // drop genuine verbatim labels like "QA" for value "qa".
  const verbatimLabelsByValue = useMemo(() => {
    const labelsByValue: Record<string, string> = {};
    (taskTypeOptions ?? []).forEach((option) => {
      if (option.label && option.label !== option.value) {
        labelsByValue[option.value.toLowerCase()] = option.label;
      }
    });
    return labelsByValue;
  }, [taskTypeOptions]);

  const options = useMemo(() => {
    // Dedupe case-variant values (e.g. "shared steps" vs "Shared Steps" from
    // a case-sensitive DISTINCT upstream), keeping the first occurrence in
    // the list's existing order. Safe because PA metric scoping matches
    // non-canonical values with raw_type__iexact, so case-variants are fully
    // interchangeable filter keys.
    const seenLowercaseValues = new Set<string>();
    const dedupedTaskTypes = (taskTypes ?? []).filter((type) => {
      const lowercaseValue = type.toLowerCase();
      if (seenLowercaseValues.has(lowercaseValue)) {
        return false;
      }
      seenLowercaseValues.add(lowercaseValue);
      return true;
    });

    const labeledOptions = dedupedTaskTypes.map((type) => ({
      value: type,
      label: verbatimLabelsByValue[type.toLowerCase()] ?? type.replace(/\b\w/g, (character) => character.toUpperCase()),
    }));

    // Distinct values can still share an identical display label (e.g. two
    // different raw types synced with the same verbatim label). Disambiguate
    // every colliding entry with its raw value so each option stays visually
    // unique; the "All work item types" entry is exempt (appended after).
    const labelCounts = new Map<string, number>();
    labeledOptions.forEach((option) => {
      labelCounts.set(option.label, (labelCounts.get(option.label) ?? 0) + 1);
    });
    const disambiguatedOptions = labeledOptions.map((option) =>
      (labelCounts.get(option.label) ?? 0) > 1 ? { ...option, label: `${option.label} (${option.value})` } : option,
    );

    return [{ value: ALL_TYPES_VALUE, label: allTypesLabel }, ...disambiguatedOptions];
  }, [allTypesLabel, taskTypes, verbatimLabelsByValue]);

  const handleChange = (value: string) => {
    if (!value || value === ALL_TYPES_VALUE) {
      setSelectedTaskType(null);
      return;
    }
    setSelectedTaskType(value);
  };

  return (
    <Select
      options={options}
      value={selectedTaskType ?? ALL_TYPES_VALUE}
      onChange={handleChange}
      size="sm"
      rightSection={
        taskTypesQuery.isLoading ? (
          <Loader
            size="xs"
            role="status"
            aria-label={t('process_analysis.task_type_filter.loading', {
              defaultValue: 'Loading work item types',
            })}
          />
        ) : undefined
      }
    />
  );
};

/**
 * Resolves the current single-entity scope used to fetch the list of task
 * types: portfolio id for Portfolios/Agents tabs, first selected team
 * for Teams tab, first selected board for Boards tab.
 *
 * @returns {{ entityType: EntityType.Portfolio | EntityType.Project | EntityType.Subproject; entityId: string | null }} Resolved scope; `entityId` is null until the user/global store has populated the active entity
 */
function useResolvedEntity(): {
  entityType: EntityType.Portfolio | EntityType.Project | EntityType.Subproject;
  entityId: string | null;
} {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);
  const portfolio = useGlobalStore((state) => state.portfolio);

  const entityType = getEntityType(activeTab) as EntityType.Portfolio | EntityType.Project | EntityType.Subproject;

  let entityId: string | null = null;
  if (entityType === EntityType.Portfolio) {
    entityId = portfolio?.id ?? null;
  } else if (entityType === EntityType.Project) {
    entityId = selectedTeams[0] ?? null;
  } else if (entityType === EntityType.Subproject) {
    entityId = selectedBoards[0] ?? null;
  }

  return { entityType, entityId };
}
